import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import {
  createSpecialityTreatment,
  updateSpecialityTreatment,
} from "api/specialityTreatment";
import {
  NAME_MAX,
  buildTreatmentPayload,
  emptySubTreatmentRow,
  removeSubTreatmentRow,
  subTreatmentRowsOf,
  validateTreatmentForm,
} from "./specialityTreatmentForm";

const nextOrderOf = (rows) =>
  rows.reduce((max, row) => Math.max(max, Number(row.displayOrder) || 0), 0) + 1;

const SpecialityTreatmentModal = ({ open, onClose, data, specialityOptions = [] }) => {
  const treatmentId = data?._id;
  const isUpdate = Boolean(treatmentId);

  const [speciality, setSpeciality] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSpeciality(
      data?.speciality && typeof data.speciality === "object" ? data.speciality : null
    );
    setName(data?.name || "");
    setIsActive(data?._id ? data.isActive !== false : true);
    setDisplayOrder(Number(data?.displayOrder) || 0);
    setRows(subTreatmentRowsOf(data));
  }, [open, data]);

  const validationError = useMemo(
    () =>
      validateTreatmentForm({
        speciality: speciality?._id,
        name,
        displayOrder,
        subTreatments: rows,
      }),
    [speciality, name, displayOrder, rows]
  );

  const updateRow = (key, patch) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const handleSubmit = async () => {
    if (validationError) return;

    setSaving(true);
    const payload = buildTreatmentPayload({
      speciality,
      name,
      isActive,
      displayOrder,
      subTreatments: rows,
    });

    try {
      if (isUpdate) {
        await updateSpecialityTreatment(treatmentId, payload);
        toast.success("Treatment updated successfully");
      } else {
        await createSpecialityTreatment(payload);
        toast.success("Treatment created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} treatment`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose("close")} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Specialty Treatment" : "Add Specialty Treatment"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <Autocomplete
            size="small"
            options={specialityOptions}
            value={speciality}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event, next) => setSpeciality(next)}
            renderInput={(params) => (
              <TextField {...params} label="Speciality" required placeholder="Select a speciality" />
            )}
          />

          <TextField
            label="Treatment Name"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: NAME_MAX, "data-testid": "treatment-name" }}
          />

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Display Order"
              type="number"
              size="small"
              value={displayOrder}
              onChange={(event) => setDisplayOrder(event.target.value)}
              inputProps={{ min: 0, step: 1, "data-testid": "treatment-order" }}
              sx={{ width: 160 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                  inputProps={{ "aria-label": "Treatment active" }}
                />
              }
              label={isActive ? "Active" : "Inactive"}
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box sx={{ fontWeight: 600, flex: 1 }}>Sub-Treatments</Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setRows((current) => [...current, emptySubTreatmentRow(nextOrderOf(current))])
                }
              >
                Add Sub-Treatment
              </Button>
            </Box>

            <Table size="small" aria-label="Sub-treatments">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell sx={{ width: 130 }}>Display Order</TableCell>
                  <TableCell sx={{ width: 120 }}>Status</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. Single Root Canal"
                        value={row.name}
                        onChange={(event) => updateRow(row.key, { name: event.target.value })}
                        inputProps={{
                          maxLength: NAME_MAX,
                          "aria-label": `Sub-treatment ${index + 1} name`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={row.displayOrder}
                        onChange={(event) =>
                          updateRow(row.key, { displayOrder: event.target.value })
                        }
                        inputProps={{
                          min: 0,
                          step: 1,
                          "aria-label": `Sub-treatment ${index + 1} display order`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={row.isActive}
                            onChange={(event) =>
                              updateRow(row.key, { isActive: event.target.checked })
                            }
                            inputProps={{ "aria-label": `Sub-treatment ${index + 1} active` }}
                          />
                        }
                        label={row.isActive ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row._id ? "Deactivate (saved rows are kept)" : "Remove"}>
                        <span>
                          <IconButton
                            aria-label={`Remove sub-treatment ${index + 1}`}
                            disabled={Boolean(row._id) && !row.isActive}
                            onClick={() =>
                              setRows((current) => {
                                const next = removeSubTreatmentRow(current, row.key);
                                return next.length ? next : [emptySubTreatmentRow(1)];
                              })
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialityTreatmentModal;
